<template>
    <div class="generation-chart chart-container">
        <!--https://www.chartjs.org/docs/latest/general/responsive.html-->
        <!--WARNING: Canvas element is set to chart-container size regardless of other elements.-->
        <canvas ref="plot"/>
    </div>
</template>

<script>
  import Chart from "chart.js";
  import math from "../../assets/js/math";
  import coord from "../../assets/js/coord";
  import gear from "../../assets/js/gear";
  import pixi from "../../assets/js/pixi/parts";
  import {circleCurve} from "../../assets/js/chart/shapes";
  import {hobCoordinates, pinionCutterCoordinates} from "../../assets/js/chart/tool";
  import {chartResize} from "../../assets/js/chart/helper";

  export default {
    name: "GeneratingDiagram",
    props: {
      tooth: Object,
      generatingRack: Object,  // must include computed params
      generatingPinion: Object,  // must include computed params
      computedPinionCutter: Object,  // PinionCutter.compute schema
      finishedRoot: Object,  // Tooth.finishedRoot schema
      internal: Boolean,
      units: Object,
    },
    diagram: {
      stepsPerPitchAngle: 5,
      steps: 12,  // nPitches * stepsPerPitchAngle
    },
    data() {
      return {
        chart: null,
        defaultResize: {},
      }
    },
    mounted() {
      // initialize
      this.newPlot();
    },
    methods: {
      newPlot() {
        // configure global settings
        // https://www.chartjs.org/docs/latest/configuration/elements.html
        // https://www.chartjs.org/docs/latest/configuration/#dataset-configuration

        // define chart
        let ctx = this.$refs.plot.getContext('2d');
        this.chart = new Chart(ctx, {
          type: 'line',
          data: {
            datasets: [],
          },
          options: {
            ...this.$defaults.chart.options,
            animation: {
              duration: 0,
            },
          }
        });

        // configure chart defaults and resize callback
        this.chart.options.elements.point.radius = 0;
        this.chart.options.elements.point.hitRadius = 3;
        this.chart.options.elements.point.hoverRadius = 3;
        this.chart.options.elements.point.borderColor = this.$defaults.colors.border;
        this.chart.options.elements.line.borderColor = this.$defaults.colors.background;
        this.chart.options.elements.line.borderWidth = 1;
        this.chart.options.elements.line.tension = 0;
        this.chart.options.elements.line.fill = false;
        this.chart.options.tooltips.enabled = false;
        this.chart.options.onResize = (chart, size) => {
          let innerRadius, outerRadius;
          const tipRadius = this.tooth.tipDiameter / 2;
          const rootRadius = this.tooth.rootDiameter / 2;
          if (this.internal) {
            innerRadius = tipRadius;
            outerRadius = rootRadius;
          } else {
            innerRadius = rootRadius;
            outerRadius = tipRadius;
          }

          // set y-limits as a function of tooth depth
          const wholeToothDepth = outerRadius - innerRadius;
          const yMin = outerRadius - 1.2 * wholeToothDepth;
          const yMax = innerRadius + 1.1 * wholeToothDepth;

          chartResize(chart, size, [yMin, yMax]);
        };
      },
      clearPlot() {
        this.chart.data.datasets = [];
        this.chart.update();
      },
      updatePlotAxes() {
        // Update plot axes to maintain an aspect ratio based on canvas size.
        // https://www.chartjs.org/docs/latest/developers/updates.html

        // timeout used to ensure resize happens
        setTimeout(() => {
          this.chart.options.onResize(this.chart);
          this.chart.update();
        }, 135)
      },
      updatePlotRack() {
        // PLACEHOLDER {UX} overwrite existing datasets and turn on chart.js animation
        // clear chart data
        this.chart.data.datasets = [];

        // compute coordinate data, then update axes
        this.computeGeneratingRack();
        this.updatePlotAxes();
      },
      updatePlotPinion() {
        // PLACEHOLDER {UX} overwrite existing datasets and turn on chart.js animation
        // clear chart data
        this.chart.data.datasets = [];

        // compute coordinate data, then update axes
        this.computeGeneratingPinion();
        this.updatePlotAxes();
      },
      computeGeneratingRack() {
        // Update chart datasets with rack generation data.

        // compute *generated* tooth params; NOT tool
        const helixAngle = math.angle2rad(this.generatingRack.helixAngle, this.units.angle);
        const numberOfTeeth = this.tooth.numberOfTeeth;
        const moduleNormal = this.generatingRack.moduleNormal;
        const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
        const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
        const pitchAngleTransverse = gear.pitchAngleTransverse1(numberOfTeeth);
        const profileShift = gear.fromCoefficient(this.generatingRack.profileShiftCoefficient, moduleNormal);

        // compute local coordinates of tool; apply profile shift
        let [cutterLocalX, cutterLocalY] = hobCoordinates(this.generatingRack, this.units.angle, 2);
        if (helixAngle !== 0) cutterLocalX = cutterLocalX.map(x => x / Math.cos(helixAngle));
        cutterLocalY = cutterLocalY.map(y => y + profileShift);

        // rolling action steps of rotation
        const zeta = pitchAngleTransverse / this.$options.diagram.stepsPerPitchAngle;
        const rollingRadius = theoreticalPitchDiameter / 2;
        const steps = this.$options.diagram.steps;

        let rollingPhi, rollingLength, rotMat, stepX, stepY, stepPoints;
        for (let i=-steps+1; i < steps; i++) {
          rollingPhi = i * zeta;
          rollingLength = rollingRadius * rollingPhi;

          // copy cutter local coord and rotate
          rotMat = coord.rotationMatrix2(rollingPhi);
          [stepX, stepY] = coord.rotatePoints2(rotMat, cutterLocalX, cutterLocalY);

          // translate coordinates based on the rack motion
          stepX = stepX.map(x => x + -rollingRadius * Math.sin(rollingPhi) + rollingLength * Math.cos(rollingPhi));
          stepY = stepY.map(y => y + rollingRadius * Math.cos(rollingPhi) + rollingLength * Math.sin(rollingPhi));

          // append to chart datasets
          stepPoints = coord.points(stepX, stepY);
          this.chart.data.datasets.push({data: stepPoints});
        }

        // plot circles and update chart
        this.computeCircles(this.generatingRack);
        this.chart.update();
      },
      computeGeneratingPinion() {
        // Update chart datasets with pinion generation data.

        const internalMultiplier = this.internal ? -1 : 1;

        // cutter params
        const cutterNumberOfTeeth = this.generatingPinion.numberOfTeeth;
        const workingCenterDistance = Math.abs(this.generatingPinion.workingCenterDistance);

        // compute *generated* tooth params; NOT tool
        const numberOfTeeth = internalMultiplier * this.tooth.numberOfTeeth;
        const pitchAngleTransverse = gear.pitchAngleTransverse1(numberOfTeeth);

        // compute local coordinates of tool
        let [cutterLocalX, cutterLocalY] = pinionCutterCoordinates(this.generatingPinion, this.computedPinionCutter, this.units.angle, 9);
        if (this.internal) cutterLocalY = cutterLocalY.map(y => -y);

        // rolling action steps of rotation
        const zeta = pitchAngleTransverse / this.$options.diagram.stepsPerPitchAngle;
        const zeta0 = zeta * numberOfTeeth / cutterNumberOfTeeth;
        const steps = this.$options.diagram.steps;

        let rotMat, stepX, stepY, stepPoints;
        for (let i=-steps+1; i < steps; i++) {
          // copy cutter local coord and rotate
          rotMat = coord.rotationMatrix2(i * zeta0);
          [stepX, stepY] = coord.rotatePoints2(rotMat, cutterLocalX, cutterLocalY);

          // translate coordinates by the working center distance
          stepY = stepY.map(y => y + workingCenterDistance);

          // rotate the coordinates by the corresponding amount of gear rotation
          rotMat = coord.rotationMatrix2(i * zeta);
          [stepX, stepY] = coord.rotatePoints2(rotMat, stepX, stepY);

          // append to chart datasets
          stepPoints = coord.points(stepX, stepY);
          this.chart.data.datasets.push({data: stepPoints});
        }

        // plot circles and update chart
        this.computeCircles(this.generatingPinion);
        this.chart.update();
      },
      computeCircles(generatingTool) {
        // Plot circles of *generated* tooth.

        // generated tooth data
        let moduleNormal = generatingTool.moduleNormal;
        let pressureAngleNormal = generatingTool.pressureAngleNormal;
        let helixAngle = generatingTool.helixAngle;  // technically *tool* helix for pinion cutter
        let numberOfTeeth = this.tooth.numberOfTeeth;
        const tipRadius = this.tooth.tipDiameter / 2;
        const rootRadius = this.tooth.rootDiameter / 2;

        // convert angular units
        pressureAngleNormal = math.angle2rad(pressureAngleNormal, this.units.angle);
        helixAngle = math.angle2rad(helixAngle, this.units.angle);

        // compute generated tooth params
        const pitchAngle = gear.pitchAngleTransverse1(numberOfTeeth);
        const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
        const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
        const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal, helixAngle});
        const baseRadius = gear.baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse}) / 2;
        const referenceRadius = theoreticalPitchDiameter / 2;  // cutting roll radius

        // root params
        const protuberanceBoundaryDiameter = this.finishedRoot.protuberanceBoundaryDiameter;
        const filletBoundaryDiameter = this.finishedRoot.filletBoundaryDiameter;
        const hasProtuberanceInvolute = filletBoundaryDiameter !== protuberanceBoundaryDiameter;
        const protuberanceBoundaryRadius = protuberanceBoundaryDiameter / 2;

        // circle settings
        const dash = [6, 6];
        const dashdot = [15, 3, 3, 3];
        const phiCenter = Math.PI / 2;
        const phiDelta = 2 * pitchAngle;
        const phiStart = phiCenter - phiDelta;
        const phiEnd = phiCenter + phiDelta;
        const xc = 0, yc = 0;
        const n = 25;

        // fillet boundary circle
        if (hasProtuberanceInvolute) {
          const filletBoundaryRadius = filletBoundaryDiameter / 2;
          const filletBoundaryPts = coord.points(...circleCurve(filletBoundaryRadius, phiStart, phiEnd, xc, yc, n));
          this.chart.data.datasets.push({
            data: filletBoundaryPts,
            borderColor: pixi.GEAR_LEGEND.filletBoundaryDiameter.color,
            borderDash: dash
          });
        }

        // protuberance boundary (form) circle
        const protubBoundaryPts = coord.points(...circleCurve(protuberanceBoundaryRadius, phiStart, phiEnd, xc, yc, n));
        this.chart.data.datasets.push({
          data: protubBoundaryPts,
          borderColor: pixi.GEAR_LEGEND.protuberanceBoundaryDiameter.color,
          borderDash: dash
        });

        // root circle
        const rootPts = coord.points(...circleCurve(rootRadius, phiStart, phiEnd, xc, yc, n));
        this.chart.data.datasets.push({
          data: rootPts,
          borderColor: pixi.GEAR_LEGEND.rootDiameter.color,
          borderDash: dash
        });

        // base circle
        const basePts = coord.points(...circleCurve(baseRadius, phiStart, phiEnd, xc, yc, n));
        this.chart.data.datasets.push({
          data: basePts,
          borderColor: pixi.GEAR_LEGEND.baseDiameter.color,
          borderDash: dash
        });

        // reference circle
        const refPts = coord.points(...circleCurve(referenceRadius, phiStart, phiEnd, xc, yc, n));
        this.chart.data.datasets.push({
          data: refPts,
          borderColor: pixi.GEAR_LEGEND.theoreticalPitchDiameter.color,
          borderDash: dashdot
        });

        // tip circle
        const tipPts = coord.points(...circleCurve(tipRadius, phiStart, phiEnd, xc, yc, n));
        this.chart.data.datasets.push({
          data: tipPts,
          borderColor: pixi.GEAR_LEGEND.tipDiameter.color,
          borderDash: dash
        });
      },
    }
  }
</script>

<style scoped>
    .generation-chart {
        padding-top: 2.5em;
        height: calc(100% - 2.5em);
        width: calc(100% - 1em);
    }
</style>
